import React, { useState } from "react";

import ShipeeziNewGrid, { GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import { Fleet } from "Models/fleet";
import { Filters, Searches, Sort, Sorts } from "Models/shipeezi.table";
import { useTranslation } from "react-i18next";
import { TypeOfMember } from "Util/enums/members";

import { getMembersTableColumns } from "../../../../../People/Members/Member/getMembersTableColumns";
import useEquipmentActions from "../../../hooks/useEquipmentActions";

interface MembersTableProps {
  fleet: Fleet;
}

export const MembersTable = (props: MembersTableProps) => {
  const { fleet } = props;
  const { t: translate } = useTranslation();
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [searches, setSearches] = useState<Searches>([]);
  const [filters, setFilters] = useState<Filters>();
  const [sorting, setSorting] = useState<Sorts>();
  const { getFleetMembersWithCount, getRoles } = useEquipmentActions();

  const {
    data: { rows: members, count: membersTotalCount },
    isFetching,
  } = getFleetMembersWithCount({
    page,
    pageSize,
    equipmentId: fleet.id,
    searches,
    filters,
    sorting,
  });

  const { data: rolesList } = getRoles();

  const handleSearch = (value: Searches) => {
    setSearches(value);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const handleSort = (sorts: Sort[]) => {
    const sorting: Sorts = [];

    sorts.forEach((sort) => sorting.push([sort.columnName, sort.direction]));

    setSorting(sorting);
  };

  const handleCurrentPage = (pageNumber: number) => setPage(pageNumber - 1);

  const columns = getMembersTableColumns({ translate }).filter(
    (column) => !["location", "userCompany", "status"].includes(column.name)
  );

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
    },
    handleSearch: {
      onSearch: handleSearch,
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("typeOfMember"),
          column: "member.typeOfMember",
          options: [TypeOfMember.STAFF, TypeOfMember.USER],
        },
        {
          name: translate("role"),
          column: "role.name",
          options: rolesList?.map((role) => role.name) || [],
        },
      ],
      onChange: handleFilter,
    },
  };

  return (
    <ShipeeziNewGrid
      data={members}
      totalItems={Number(membersTotalCount)}
      columns={columns}
      showSelection={false}
      showSort={false}
      currentPage={page}
      loadingData={isFetching}
      pageSize={pageSize}
      pageSizeList={[10, 20, 40, 60]}
      onChangePageSize={setPageSize}
      onNextPage={handleCurrentPage}
      onPreviousPage={handleCurrentPage}
      onGoToPage={handleCurrentPage}
      positionActionsColumn="right"
      keyExtractor={(data) => data?.id?.toString()}
      emptyData={translate("noInformationToBeShown")}
      onSorting={handleSort}
      generalActions={generalActions}
    />
  );
};
